// app/javascript/controllers/date_picker_controller.js
// Rule: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
import { Controller } from "@hotwired/stimulus"
import Datepicker from 'flowbite-datepicker/Datepicker'

export default class extends Controller {
  static targets = [
    "startDate", "endDate", 
    "startInput", "endInput", 
    "selectedRange", "minimumStayWarning", 
    "minimumStayValue", "submitButton",
    "autoAdjust"
  ]
  
  static values = { 
    minimumStay: { type: Number, default: 2 },
    minDays: { type: Number, default: 1 },
    maxDays: { type: Number, default: 14 },
    unavailableDates: Array,
    startDate: String,
    endDate: String,
    autoAdjust: { type: Boolean, default: true }
  }

  connect() {
    console.log('Date picker controller connected')

    // Get values from data attributes
    this.minimumStayValue = parseInt(this.minimumStayTarget?.value || this.element.dataset.datepickerMinStayValue || 2)
    this.autoAdjustValue = this.hasAutoAdjustTarget ? this.autoAdjustTarget.checked : true

    // Flag to prevent infinite recursion during date changes
    this.isProcessingDateChange = false
    
    // Initialize date values from URL parameters first, then from defaults
    const urlParams = new URLSearchParams(window.location.search)
    const startDateParam = urlParams.get('start_date')
    const endDateParam = urlParams.get('end_date')
    
    // Set default date values
    const today = new Date()
    
    // Always prioritize URL parameters, never use cached values
    if (this.hasStartInputTarget) {
      try {
        if (startDateParam) {
          const startDate = this.parseDate(startDateParam)
          if (startDate) {
            this.startInputTarget.value = this.formatDateForDisplay(startDate)
            this.startDateValue = this.formatDateForDisplay(startDate)
          } else {
            // Default to today if URL param is invalid
            this.startInputTarget.value = this.formatDateForDisplay(today)
            this.startDateValue = this.formatDateForDisplay(today)
          }
        } else {
          // No URL param, use today
          this.startInputTarget.value = this.formatDateForDisplay(today)
          this.startDateValue = this.formatDateForDisplay(today)
        }
      } catch (e) {
        console.error('Error setting start date from URL:', e)
        this.startInputTarget.value = this.formatDateForDisplay(today)
        this.startDateValue = this.formatDateForDisplay(today)
      }
    }
    
    if (this.hasEndInputTarget) {
      try {
        if (endDateParam) {
          const endDate = this.parseDate(endDateParam)
          if (endDate) {
            this.endInputTarget.value = this.formatDateForDisplay(endDate)
            this.endDateValue = this.formatDateForDisplay(endDate)
          } else {
            // Default to today + minimum stay if URL param is invalid
            const defaultEndDate = new Date(today)
            defaultEndDate.setDate(defaultEndDate.getDate() + this.minimumStayValue)
            this.endInputTarget.value = this.formatDateForDisplay(defaultEndDate)
            this.endDateValue = this.formatDateForDisplay(defaultEndDate)
          }
        } else {
          // No URL param, use today + minimum stay
          const defaultEndDate = new Date(today)
          defaultEndDate.setDate(defaultEndDate.getDate() + this.minimumStayValue)
          this.endInputTarget.value = this.formatDateForDisplay(defaultEndDate)
          this.endDateValue = this.formatDateForDisplay(defaultEndDate)
        }
      } catch (e) {
        console.error('Error setting end date from URL:', e)
        const defaultEndDate = new Date(today)
        defaultEndDate.setDate(defaultEndDate.getDate() + this.minimumStayValue)
        this.endInputTarget.value = this.formatDateForDisplay(defaultEndDate)
        this.endDateValue = this.formatDateForDisplay(defaultEndDate)
      }
    }
    
    // Initialize date pickers
    this.initDatePickers()

    // Update the selected range display
    this.updateSelectedRangeDisplay()

    // Update the minimum stay value in the warning message
    if (this.hasMinimumStayValueTarget) {
      this.minimumStayValueTarget.textContent = this.minimumStayValue
    }

    // Check minimum stay requirement
    this.checkMinimumStay()

    // Listen for URL parameter changes
    window.addEventListener('popstate', this.handlePopState.bind(this))

    // Pathway 2: outside-click dismissal
    this._outsideClickHandler = (event) => {
      // The flowbite popover renders on <body>, outside this controller's
      // element, so a mousedown on a calendar cell is not contained here.
      // Treat clicks inside a .datepicker popover as inside — otherwise we
      // hide the picker on mousedown before the click can select the date.
      if (this.element.contains(event.target)) return
      if (event.target.closest?.(".datepicker")) return
      this.startDatepicker?.hide()
      this.endDatepicker?.hide()
    }
    document.addEventListener('mousedown', this._outsideClickHandler)

    // Pathway 3: hide popovers when the parent form submits
    const form = this.element.closest('form') || (this.element.tagName === 'FORM' ? this.element : null)
    if (form) {
      this._submitHandler = () => {
        this.startDatepicker?.hide()
        this.endDatepicker?.hide()
      }
      form.addEventListener('submit', this._submitHandler)
      this._boundForm = form
    }
  }

  disconnect() {
    console.log("DatePicker controller disconnected")

    // Clean up event listeners
    window.removeEventListener('popstate', this.handlePopState.bind(this))
    if (this._outsideClickHandler) {
      document.removeEventListener('mousedown', this._outsideClickHandler)
    }
    if (this._boundForm && this._submitHandler) {
      this._boundForm.removeEventListener('submit', this._submitHandler)
    }
    
    // Destroy datepickers to prevent memory leaks
    if (this.startDatepicker) {
      this.startDatepicker.destroy()
      this.startDatepicker = null
    }
    
    if (this.endDatepicker) {
      this.endDatepicker.destroy()
      this.endDatepicker = null
    }
  }
  
  handlePopState() {
    // Re-initialize date pickers when URL parameters change
    this.resetDatePickers()
  }
  
  syncFromUrlParams() {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const startDateParam = urlParams.get('start_date')
    const endDateParam = urlParams.get('end_date')
    
    // Update input values if URL parameters exist
    if (startDateParam && this.hasStartInputTarget) {
      try {
        const startDate = this.parseDate(startDateParam)
        if (startDate) {
          this.startInputTarget.value = this.formatDateForDisplay(startDate)
          this.startDateValue = this.formatDateForDisplay(startDate)
          if (this.startDatepicker) {
            this.startDatepicker.setDate(startDate)
          }
        }
      } catch (e) {
        console.error('Error parsing start date from URL:', e)
      }
    }
    
    if (endDateParam && this.hasEndInputTarget) {
      try {
        const endDate = this.parseDate(endDateParam)
        if (endDate) {
          this.endInputTarget.value = this.formatDateForDisplay(endDate)
          this.endDateValue = this.formatDateForDisplay(endDate)
          if (this.endDatepicker) {
            this.endDatepicker.setDate(endDate)
          }
        }
      } catch (e) {
        console.error('Error parsing end date from URL:', e)
      }
    }
    
    // Update the selected range display
    this.updateSelectedRangeDisplay()
    
    // Update the minimum stay value in the warning message
    if (this.hasMinimumStayValueTarget) {
      this.minimumStayValueTarget.textContent = this.minimumStayValue
    }
    
    // Check minimum stay
    this.checkMinimumStay()
  }
  
  handleStartDateChange(event) {
    // Prevent infinite recursion
    if (this.isProcessingDateChange) return
    this.isProcessingDateChange = true
    
    try {
      console.log("Start date changed:", event.target.value)
      
      // Parse the selected date
      const selectedDate = this.parseDate(event.target.value)
      
      // If the date is invalid, reset to the current value and exit
      if (!selectedDate) {
        console.log("Invalid start date, resetting")
        event.target.value = this.startDateValue
        this.isProcessingDateChange = false
        return
      }
      
      // Update the start date value
      this.startDateValue = this.formatDateForDisplay(selectedDate)
      
      // Update the hidden field value
      const hiddenStartDateField = document.getElementById('hidden_start_date')
      if (hiddenStartDateField) {
        hiddenStartDateField.value = this.startDateValue
      }
      
      // If auto-adjust is enabled, check if the end date needs to be adjusted
      if (this.autoAdjustValue) {
        // Get the current end date
        const endDate = this.parseDate(this.endDateValue)
        
        // Calculate the minimum end date based on the selected start date and minimum stay
        const minEndDate = new Date(selectedDate)
        minEndDate.setDate(minEndDate.getDate() + this.minimumStayValue)
        
        // If the current end date is before the minimum end date, update it
        if (endDate < minEndDate) {
          console.log("Adjusting end date to meet minimum stay requirement")
          this.endDateValue = this.formatDateForDisplay(minEndDate)
          
          // Update the end date input
          if (this.hasEndInputTarget) {
            this.endInputTarget.value = this.endDateValue
          }
          
          // Update the end date picker
          if (this.endDatepicker) {
            this.endDatepicker.setDate(minEndDate)
          }
          
          // Update the hidden end date field
          const hiddenEndDateField = document.getElementById('hidden_end_date')
          if (hiddenEndDateField) {
            hiddenEndDateField.value = this.endDateValue
          }
        }
      }
      
      // Check if the selected date range meets minimum stay requirements
      this.checkMinimumStay()
      
      // Update the selected range display
      this.updateSelectedRangeDisplay()
    } catch (e) {
      console.error("Error handling start date change:", e)
    } finally {
      this.isProcessingDateChange = false
    }
  }
  
  handleEndDateChange(event) {
    // Prevent infinite recursion
    if (this.isProcessingDateChange) return
    this.isProcessingDateChange = true
    
    try {
      console.log("End date changed:", event.target.value)
      
      // Parse the selected date
      const selectedDate = this.parseDate(event.target.value)
      
      // If the date is invalid, reset to the current value and exit
      if (!selectedDate) {
        console.log("Invalid end date, resetting")
        event.target.value = this.endDateValue
        this.isProcessingDateChange = false
        return
      }
      
      // Update the end date value
      this.endDateValue = this.formatDateForDisplay(selectedDate)
      
      // Update the hidden field value
      const hiddenEndDateField = document.getElementById('hidden_end_date')
      if (hiddenEndDateField) {
        hiddenEndDateField.value = this.endDateValue
      }
      
      // Check if the selected date range meets minimum stay requirements
      this.checkMinimumStay()
      
      // Update the selected range display
      this.updateSelectedRangeDisplay()
    } catch (e) {
      console.error("Error handling end date change:", e)
    } finally {
      this.isProcessingDateChange = false
    }
  }
  
  // Check if the selected date range meets minimum stay requirements
  checkMinimumStay() {
    if (!this.startDateValue || !this.endDateValue) return
    
    try {
      const startDate = this.parseDate(this.startDateValue)
      const endDate = this.parseDate(this.endDateValue)
      
      // Check for invalid dates
      if (!startDate || !endDate) {
        console.error("Invalid date format detected")
        return
      }
      
      // Calculate the number of nights
      const nights = this.calculateNights(startDate, endDate)
      console.log(`Nights calculated: ${nights}, minimum required: ${this.minimumStayValue}`)
      
      // Show warning if minimum stay requirement is not met
      if (nights < this.minimumStayValue) {
        this.showMinimumStayWarning(nights)
        
        // Auto-adjust if enabled
        if (this.autoAdjustValue && !this.isProcessingDateChange) {
          this.adjustToMinimumStay()
        }
        
        // Disable submit button if present
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = true
          this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
        }
      } else {
        this.hideMinimumStayWarning()
        
        // Enable submit button if present
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false
          this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        }
      }
    } catch (error) {
      console.error("Error in checkMinimumStay:", error)
    }
  }
  
  // Calculate the number of nights between two dates
  calculateNights(startDate, endDate) {
    // Calculate the difference in milliseconds
    const diffTime = Math.abs(endDate - startDate)
    // Convert to days
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }
  
  // Show the minimum stay warning with appropriate message
  showMinimumStayWarning(currentNights) {
    if (!this.hasMinimumStayWarningTarget) return
    
    // Create warning message
    let message = ''
    if (currentNights === 0) {
      message = `Please select a valid date range. Minimum stay is ${this.minimumStayValue} nights.`
    } else if (currentNights === 1) {
      message = `Your selected stay is only ${currentNights} night. Minimum stay is ${this.minimumStayValue} nights.`
    } else {
      message = `Your selected stay is only ${currentNights} nights. Minimum stay is ${this.minimumStayValue} nights.`
    }
    
    // Update warning message
    this.minimumStayWarningTarget.textContent = message
    
    // Show warning
    this.minimumStayWarningTarget.classList.remove('hidden')
  }
  
  // Hide the minimum stay warning
  hideMinimumStayWarning() {
    if (!this.hasMinimumStayWarningTarget) return
    
    // Hide warning
    this.minimumStayWarningTarget.classList.add('hidden')
  }
  
  // Automatically adjust the end date to meet minimum stay requirements
  adjustToMinimumStay() {
    if (!this.startDateValue) return
    
    try {
      // Set flag to prevent recursion
      this.isProcessingDateChange = true
      
      const startDate = this.parseDate(this.startDateValue)
      
      // Check for invalid date
      if (!startDate) {
        console.error("Invalid start date format detected")
        this.isProcessingDateChange = false
        return
      }
      
      // Calculate the new end date based on minimum stay
      const endDate = new Date(startDate)
      endDate.setDate(startDate.getDate() + this.minimumStayValue)
      
      // Update the end date input
      if (this.hasEndInputTarget) {
        this.endInputTarget.value = this.formatDateForDisplay(endDate)
        this.endDateValue = this.formatDateForDisplay(endDate)
        
        // Update the datepicker if it exists
        if (this.endDatepicker) {
          try {
            this.endDatepicker.setDate(endDate)
          } catch (e) {
            console.error('Error setting end date in datepicker:', e)
          }
        }
      }
      
      // Hide the warning
      this.hideMinimumStayWarning()
      
      // Enable submit button if present
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      }
      
      // Update the selected range display
      this.updateSelectedRangeDisplay()
      
      // Reset flag after a short delay
      setTimeout(() => {
        this.isProcessingDateChange = false
      }, 100)
    } catch (error) {
      console.error("Error in adjustToMinimumStay:", error)
      this.isProcessingDateChange = false
    }
  }
  
  updateSelectedRangeDisplay() {
    if (!this.hasSelectedRangeTarget) return
    
    try {
      // Get start and end dates
      const startDate = this.hasStartInputTarget ? this.parseDate(this.startInputTarget.value) : null
      const endDate = this.hasEndInputTarget ? this.parseDate(this.endInputTarget.value) : null
      
      // Only update if both dates are valid
      if (startDate && endDate) {
        // Calculate nights between dates
        const nights = this.calculateNights(startDate, endDate)
        
        // Format dates for display
        const formattedStartDate = this.formatDisplayDate(startDate)
        const formattedEndDate = this.formatDisplayDate(endDate)
        
        // Update display text
        const displayText = `${formattedStartDate} to ${formattedEndDate} (${nights} night${nights !== 1 ? 's' : ''})`
        
        const spanElement = this.selectedRangeTarget.querySelector('span')
        if (spanElement) {
          spanElement.textContent = displayText
        }
        
        // Show the selected range display
        this.selectedRangeTarget.classList.remove('hidden')
      } else {
        // Hide the selected range display if dates are invalid
        this.selectedRangeTarget.classList.add('hidden')
      }
    } catch (e) {
      console.error('Error updating selected range display:', e)
      // Hide the selected range display on error
      this.selectedRangeTarget.classList.add('hidden')
    }
  }
  
  parseDate(dateString) {
    if (!dateString) return null
    
    try {
      // Try to parse MM/DD/YYYY format
      if (dateString.includes('/')) {
        const [month, day, year] = dateString.split('/')
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      }
      
      // Try to parse YYYY-MM-DD format (for backward compatibility)
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-')
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      }
      
      // If it's already a Date object, return it
      if (dateString instanceof Date) {
        return dateString
      }
      
      // Try to parse using Date constructor as fallback
      const date = new Date(dateString)
      return isNaN(date.getTime()) ? null : date
    } catch (e) {
      console.error('Error parsing date:', e)
      return null
    }
  }
  
  formatDisplayDate(date) {
    if (!date) return ''
    
    // Format date as Month Day, Year (e.g., "March 15, 2023")
    const options = { year: 'numeric', month: 'long', day: 'numeric' }
    return date.toLocaleDateString('en-US', options)
  }
  
  formatDateForDisplay(date) {
    if (!date) return ''
    
    // Format date as MM/DD/YYYY
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    
    return `${month}/${day}/${year}`
  }
  
  initDatePickers() {
    const today = new Date()
    
    // Initialize start date picker
    if (this.hasStartInputTarget) {
      const startDateElement = this.startInputTarget
      
      try {
        // Ensure the input is treated as text, not date
        startDateElement.setAttribute('type', 'text')
        
        // Prevent mobile browsers from showing their date picker
        startDateElement.setAttribute('readonly', 'readonly')
        startDateElement.setAttribute('inputmode', 'none')
        
        // Create Flowbite datepicker for start date
        this.startDatepicker = new Datepicker(startDateElement, {
          format: 'mm/dd/yyyy',
          minDate: today,
          defaultDate: this.parseDate(startDateElement.value) || today,
          autohide: true,
          todayBtn: true,
          clearBtn: false,
          todayBtnMode: 1,
          showOnFocus: true,
          disableTouchKeyboard: true
        })

        // Pathway 1 (start side): hide end popover when start input gains focus
        startDateElement.addEventListener('focus', () => this.endDatepicker?.hide())

        // Add event listener for date selection
        startDateElement.addEventListener('changeDate', (event) => {
          if (event && event.detail && event.detail.date) {
            startDateElement.value = this.formatDateForDisplay(event.detail.date)
            this.handleStartDateChange({ target: startDateElement })
          }
        })
        
        // Allow clicking on the input to open the datepicker
        startDateElement.addEventListener('click', () => {
          this.startDatepicker.show()
        })
        
        console.log('Start date picker initialized successfully')
      } catch (e) {
        console.error('Error initializing start date picker:', e)
      }
    }
    
    // Initialize end date picker
    if (this.hasEndInputTarget) {
      const endDateElement = this.endInputTarget
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      
      try {
        // Ensure the input is treated as text, not date
        endDateElement.setAttribute('type', 'text')
        
        // Prevent mobile browsers from showing their date picker
        endDateElement.setAttribute('readonly', 'readonly')
        endDateElement.setAttribute('inputmode', 'none')
        
        // Create Flowbite datepicker for end date
        this.endDatepicker = new Datepicker(endDateElement, {
          format: 'mm/dd/yyyy',
          minDate: tomorrow,
          defaultDate: this.parseDate(endDateElement.value) || tomorrow,
          autohide: true,
          todayBtn: true,
          clearBtn: false,
          todayBtnMode: 1,
          showOnFocus: true,
          disableTouchKeyboard: true
        })

        // Pathway 1 (end side): hide start popover when end input gains focus
        endDateElement.addEventListener('focus', () => this.startDatepicker?.hide())

        // Add event listener for date selection
        endDateElement.addEventListener('changeDate', (event) => {
          if (event && event.detail && event.detail.date) {
            endDateElement.value = this.formatDateForDisplay(event.detail.date)
            this.handleEndDateChange({ target: endDateElement })
          }
        })
        
        // Allow clicking on the input to open the datepicker
        endDateElement.addEventListener('click', () => {
          this.endDatepicker.show()
        })
        
        console.log('End date picker initialized successfully')
      } catch (e) {
        console.error('Error initializing end date picker:', e)
      }
    }
  }
  
  // Reset date pickers with fresh values
  resetDatePickers() {
    console.log("Resetting date pickers")
    
    // Destroy existing date pickers
    if (this.startDatepicker) {
      this.startDatepicker.destroy()
      this.startDatepicker = null
    }
    
    if (this.endDatepicker) {
      this.endDatepicker.destroy()
      this.endDatepicker = null
    }
    
    // Clear cached values
    this.startDateValue = null
    this.endDateValue = null
    
    // Re-initialize with fresh values
    this.connect()
  }
}